import {
  CancelablePromise,
  SuccessCollectionTaskResponse,
  TaskControllerService,
  TaskResponse,
  TaskUiElementsResponse,
  UpdateTaskRequest
} from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import { log, warn } from "lib/log";
import { globalEvents } from "src/constants/globalEvents";
import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { FeatureFlagName, featureFlags } from "src/lib/featureFlags";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import OnboardingBloc from "src/state/OnboardingBloc/OnboardingBloc";
import { KnownGroup, KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import type {
  WebsocketTaskStatusUpdatedPayload,
  WebsocketTaskUpdatedPayload
} from "../WebSocketBloc/WebSocketBloc";
import WebSocketBloc, {
  WebsocketLifecycleEvent,
  WebsocketMessageType
} from "../WebSocketBloc/WebSocketBloc";
import { PROGRAM_FIRST_LESSON } from "src/ui/components/UserJourneyOverview/journeyConfig";
import Path from "src/constants/path";
import { AppPopup } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import { consistencyScoreState } from "../state";

export type AppTaskDetailsType = "questionnaire" | "video";

export interface AppTaskDetails {
  startedDate?: Date;
  completedDate?: Date;
  availableDate?: Date;
  availableDateEnd?: Date;
  startsInFuture?: boolean;
  timeTillAvailable?: Date;
  timeTillAvailableEnd?: Date;
  videoId?: number;
  videoProvider?: "vimeo" | "youtube";
  questionnaireId?: string;
  type?: AppTaskDetailsType;
}

export type TaskObserverCallback = (
  programs: Map<KnownProgram, TaskResponseKnown[]>
) => void;

export enum TaskObserverEvent {
  TASK_LIST_CHANGED = "task-list-changed"
}

/**
 * TaskKey is the key used to identify a task in the TaskManagementBloc.
 * it is used for creating configurations for tasks.
 * the key can be any string, but its recommended to follow the convention:
 * [program].[group].[slug]
 * or [group].[slug]
 * or [slug]
 *
 * the configs will be checked in the order described above. with the most specific config being used.
 */
export enum TaskKey {
  LOG_WEIGHT = "log-weight",
  UPLOAD_LABS = "upload_lab_report",
  SELECT_PCP = "select-pcp",
  PHARMACY_INSURANCE = "pharmacy-insurance",
  MEDICAL_INSURANCE = "medical-insurance",
  PREFERRED_PHARMACY = "preferred-pharmacy",
  SCHEDULE_GETLABS = "schedule-labs",
  CHECKIN_QUESTIONNAIRE = "checkin-questionnaire",
  RECURRING_CHECKIN_QUESTIONNAIRE = "care.checkin.checkin-questionnaire",
  AMAZON_WEIGHT_JOURNEY_WEEK1 = "awj-week1",
  RXDIET_MEAL_PLAN_SETUP = "mealplan.rxdiet.setup",
  ACTIVATE_HEALTH_SYNC = "activate-health-sync",
  COMPLETE_INITIAL_LAB_ORDER = "complete-initial-lab-order",
  COMPLETE_RECURRING_LAB_ORDER = "complete-recurring-labs",
  INITIAL_LAB_QUESTIONNAIRE = "initial-lab-questionnaire",
  INITIAL_SYNC_VISIT = "initial-sync-visit",
  COMPLETE_ONBOARDING = "completed-onboarding",
  PRE_COMPLETE_ONBOARDING = "completed-pre-onboarding",
  ONBOARDING_CONSOLIDATED = "onboarding-consolidated",
  PRE_ONBOARDING_SYNC_VISIT = "initial-pre-onboarding-sync-visit",
  INITIAL_APPLICATION_DATE = "initial-application-date",
  BEHAVIORAL_HEALTH = "bh-screening.amazon.bh-screening-questionnaires",
  CHOOSE_NEXT_JOURNEY = "choose-next-journey",
  PHYSICAL_ACTIVITY_JOURNEY = "physical-activity-journey",
  MINDSET_JOURNEY = "mindset-journey",
  NUTRITION_JOURNEY = "nutrition-journey",
  ONBOARDING_LABS_CONSOLIDATED = "onboarding-labs-consolidated",
  COACHING_RATING = "rate",
  WAIST_MEASUREMENT = "waist-measurement",
  GLP1_REFILL_SURVEY = "glp1-refill",
  GLP1_REFILL_SURVEY_CONSOLIDATED = "consolidated-glp1-refill",
  CONFIRM_MOST_RECENT_INTAKE = "glp1-continuation-questionnaire",
  UPLOAD_PRESCRIPTION_LABEL = "upload-prescription-label",
  UPLOAD_PROOF_OF_WEIGHT = "upload-proof-of-weight"
}

export enum TaskActionCall {
  onboardingBlocCtaActionStepGetYourLabsDone = "onboardingBlocCtaActionStepGetYourLabsDone"
}

export const onboardingTasksToShow = [
  TaskKey.PHARMACY_INSURANCE,
  TaskKey.MEDICAL_INSURANCE,
  TaskKey.CHECKIN_QUESTIONNAIRE,
  TaskKey.SELECT_PCP,
  TaskKey.PREFERRED_PHARMACY
];

export const glpContinuationTasksToShow = [
  TaskKey.CONFIRM_MOST_RECENT_INTAKE,
  TaskKey.UPLOAD_PRESCRIPTION_LABEL,
  TaskKey.UPLOAD_PROOF_OF_WEIGHT,
  TaskKey.PHARMACY_INSURANCE,
  TaskKey.PREFERRED_PHARMACY
];

/**
 * Task key for the consolidated GLP continuation task
 */
export const GLP_CONTINUATION_CONSOLIDATED =
  "glp-continuation-consolidated" as TaskKey;

export type KnownTaskSlugs =
  | "log-weight"
  | "activate-health-sync"
  | "upload-labs"
  | "upload_lab_report"
  | "select-pcp"
  | "pharmacy-insurance"
  | "medical-insurance"
  | "preferred-pharmacy"
  | "schedule-labs"
  | "checkin-questionnaire"
  | "initial-sync-visit"
  | "awj-week1"
  | "setup"
  | "initial-application-date"
  | "choose-next-journey"
  | "rate"
  | "confirm-the-most-recent-intake"
  | "upload-prescription-label"
  | "upload-proof-of-weight";

export interface AppTaskResponseAdditionalFields {
  vimeoVideoId?: number;
  questionnaireRef?: {
    id: string;
    type: "TYPEFORM";
  };
}

export interface TaskResponseKnown
  extends Omit<TaskResponse, "additionalData"> {
  slug: KnownTaskSlugs | string;
  program: KnownProgram | string;
  additionalData?: Record<string, unknown>;
  updatedAt: string;
  createdAt: string;
}

interface TaskManagementBlocState {
  tasks: TaskResponseKnown[];
  consolidationEnabled: boolean;
}

export default class TaskManagementBloc extends Cubit<TaskManagementBlocState> {
  static keepAlive = true;
  activePrograms = new Set<KnownProgram>();

  /**
   * Programs that should be treated as education/journey programs
   */
  private readonly educationPrograms = [
    KnownProgram.LIFEBALANCE,
    KnownProgram.EDUCATION,
    KnownProgram.NUTRITION_JOURNEY,
    KnownProgram.MINDSET_JOURNEY,
    KnownProgram.PHYSICAL_ACTIVITY_JOURNEY,
    KnownProgram.EVERYDAY_DIABETES_JOURNEY
  ] as const;

  constructor() {
    super({
      tasks: [],
      consolidationEnabled: true
    });
    this.addEventListeners();

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.activePrograms.clear();
      this.emit({
        tasks: [],
        consolidationEnabled: true
      });
    });
  }

  log = (message: string, etc: unknown = ""): void => {
    if (featureFlags.getFlag(FeatureFlagName.loggingTaskManagement)) {
      // eslint-disable-next-line no-console
      console.info(`[TaskManagementBloc]: ${message}`, etc);
    }
    logSentryBreadcrumb("taskManagement", message);
  };

  /**
   * Loads all tasks for a program, for the current user.
   * Updates the state with the new tasks.
   * @param program, only tasks for this program will be loaded.
   * @returns The loaded tasks.
   */
  loadProgramTasks = async (
    program: KnownProgram
  ): Promise<TaskResponseKnown[] | undefined> => {
    return new Promise<TaskResponseKnown[] | undefined>((resolve, reject) => {
      const mocked = this.getMockedProgramTasks(program);
      if (mocked) {
        this.log(
          `loadProgramTasks: ${program} loaded ${mocked.length} tasks from mock.`
        );
        this.addTasksToState(mocked, program);
        resolve(mocked);
        return;
      }

      const cached = this.fetchRequestStore.get(program);
      const request = cached
        ? cached
        : TaskControllerService.getProgramTasks(program);

      this.fetchRequestStore.set(program, request);
      if (typeof request === "undefined") {
        reportErrorSentry(new Error("No request for program tasks"));
        return;
      }

      request
        .then((response) => {
          this.addTasksToState(response.data as TaskResponseKnown[], program);

          this.log(
            `loadProgramTasks: ${program} loaded ${response.data.length} tasks.`,
            "setting program active"
          );
          this.activePrograms.add(program);

          resolve(response.data as TaskResponseKnown[]);
        })
        .catch((error: unknown) => {
          reject(error);
        })
        .finally(() => {
          this.fetchRequestStore.delete(program);
        });
    });
  };
  fetchRequestStore: Map<
    string,
    CancelablePromise<SuccessCollectionTaskResponse>
  > = new Map();

  observers: [TaskObserverEvent, TaskObserverCallback][] = [];
  addObserver = (
    event: TaskObserverEvent,
    observer: TaskObserverCallback
  ): (() => void) => {
    this.observers.push([event, observer]);
    return () => this.removeObserver(observer);
  };
  removeObserver = (observer: TaskObserverCallback): void => {
    const index = this.observers.findIndex(([, o]) => o === observer);
    if (index === -1) return;
    this.observers.splice(index, 1);
  };

  /**
   * Adds tasks to the state. Removes duplicates based on path (program.group.slug).
   * When multiple tasks have the same path, keeps the one with the latest updatedAt timestamp.
   * @param tasks The tasks to add.
   */

  addTasksToState = (
    tasks: TaskResponseKnown[],
    program?: KnownProgram
  ): void => {
    this.log(
      `addTasksToState: adding ${tasks.length} tasks${
        program ? ` for program ${program}` : ""
      }`
    );

    const filteredExisting = this.state.tasks.filter((t) => {
      if (program && t.program === program) {
        return Boolean(t.id);
      }
      return true;
    });

    const allTasks = [...filteredExisting, ...tasks];

    const uniqueTasksMap = new Map<string, TaskResponseKnown>();

    for (const addingTask of allTasks) {
      const taskPath = this.getTaskPath(addingTask);
      const existingTask = uniqueTasksMap.get(taskPath);

      if (!existingTask) {
        uniqueTasksMap.set(taskPath, addingTask);
      } else {
        const existingUpdatedAt = new Date(
          existingTask.updatedAt || "1970-01-01"
        );
        const addingUpdatedAt = new Date(addingTask.updatedAt || "1970-01-01");

        if (addingUpdatedAt >= existingUpdatedAt) {
          uniqueTasksMap.set(taskPath, addingTask);
        }
      }
    }

    const uniqueTasks = Array.from(uniqueTasksMap.values());

    const programs = new Map<KnownProgram, TaskResponseKnown[]>();

    uniqueTasks.forEach((task) => {
      const programTasks = programs.get(task.program as KnownProgram) ?? [];
      programs.set(task.program as KnownProgram, [...programTasks, task]);
    });

    this.emit({
      ...this.state,
      tasks: uniqueTasks
    });

    this.observers.forEach(([event, observer]) => {
      if (event === TaskObserverEvent.TASK_LIST_CHANGED) {
        observer(programs);
      }
    });
  };

  /**
   * Handles new task creation from WebSocket.
   * Reloads the program tasks to fetch the complete task data.
   * @param task The WebSocket payload containing basic task info
   */
  handleTaskCreated = (task?: WebsocketTaskUpdatedPayload): void => {
    if (!task?.program) {
      this.log(`WS_TASK_CREATED_INVALID: Invalid task payload`, task);
      return;
    }

    this.log(`WS_TASK_CREATED: ${this.getTaskPath(task)} - reloading program`);
    void this.loadProgramTasks(task.program as KnownProgram);
  };

  /**
   * Handles comprehensive task updates from WebSocket including all task fields.
   * @param task The WebSocket payload containing full task update data
   */
  handleTaskUpdated = (task?: WebsocketTaskUpdatedPayload): void => {
    if (!task || !task.program || !task.group || !task.slug) {
      this.log(`WS_FULL_UPDATE_INVALID: Invalid task payload`, task);
      return;
    }

    const taskPath = this.getTaskPath(task);
    this.log(`WS_FULL_UPDATE_START: ${taskPath}`, task);

    const existingTask = this.state.tasks.find(
      (t) => this.getTaskPath(t) === taskPath
    );
    if (!existingTask) {
      this.log(
        `WS_FULL_UPDATE_NO_EXISTING: ${taskPath} - task not found, reloading program tasks`
      );
      void this.loadProgramTasks(task.program as KnownProgram);
      return;
    }

    this.log(
      `WS_FULL_UPDATE_EXISTING: ${taskPath} | CurrentStatus: ${existingTask.status} | UpdatedAt: ${existingTask.updatedAt}`
    );

    const currentTimestamp = new Date().toISOString();
    const updatedTasks: TaskResponseKnown[] = this.state.tasks.map((t) => {
      if (this.getTaskPath(t) === taskPath) {
        // Start with existing task
        const updatedTask: TaskResponseKnown = { ...t };

        // Update fields if provided (direct assignment, no deep merge)
        if (task.status !== undefined) {
          updatedTask.status = task.status as TaskResponse.status;
        }
        if (task.availableFrom !== undefined) {
          updatedTask.availableFrom = task.availableFrom;
        }
        if (task.availableTo !== undefined) {
          updatedTask.availableTo = task.availableTo;
        }
        if (task.startedAt !== undefined) {
          updatedTask.startedAt = task.startedAt;
        }
        if (task.completedAt !== undefined) {
          updatedTask.completedAt = task.completedAt;
        }

        // Direct assignment for complex fields (WebSocket has complete data)
        if (task.additionalData !== undefined) {
          updatedTask.additionalData = task.additionalData;
        }
        if (task.uiElements !== undefined) {
          updatedTask.uiElements =
            task.uiElements as typeof updatedTask.uiElements;
        }
        if (task.behavior !== undefined) {
          updatedTask.behavior = task.behavior as typeof updatedTask.behavior;
        }

        // Always update timestamp to ensure proper deduplication
        updatedTask.updatedAt = currentTimestamp;

        this.log(
          `WS_FULL_UPDATE_APPLIED: ${taskPath} | Status: ${t.status} → ${updatedTask.status} | UpdatedAt: ${updatedTask.updatedAt}`
        );
        return updatedTask;
      }

      return t;
    });

    this.log(
      `WS_FULL_UPDATE_COMPLETE: ${taskPath} | Calling addTasksToState with ${updatedTasks.length} tasks`
    );
    this.addTasksToState(updatedTasks);
  };

  getTaskPath = (
    task:
      | TaskResponseKnown
      | WebsocketTaskStatusUpdatedPayload
      | WebsocketTaskUpdatedPayload
      | Partial<TaskResponse>
  ): string => {
    return `${task.program}/${task.group}/${task.slug}`;
  };

  handleTaskStatusUpdated = (
    task?: WebsocketTaskStatusUpdatedPayload
  ): void => {
    if (!task) return;
    const taskPath = this.getTaskPath(task);

    this.log(
      `WS_UPDATE_START: ${taskPath} | OldStatus: ${task.oldStatus} | NewStatus: ${task.newStatus}`
    );

    const existingTask = this.state.tasks.find(
      (t) => this.getTaskPath(t) === taskPath
    );
    if (!existingTask) {
      this.log(
        `WS_NO_EXISTING_TASK: ${taskPath} - task not found, reloading program tasks`
      );
      void this.loadProgramTasks(task.program as KnownProgram);
      return;
    }

    this.log(
      `WS_EXISTING_TASK: ${taskPath} | CurrentStatus: ${existingTask.status} | UpdatedAt: ${existingTask.updatedAt}`
    );

    const currentTimestamp = new Date().toISOString();
    const updatedTasks: TaskResponseKnown[] = this.state.tasks.map((t) => {
      if (this.getTaskPath(t) === taskPath) {
        const updatedTask = {
          ...t,
          status: task.newStatus,
          updatedAt: currentTimestamp // Update timestamp to current time to ensure proper deduplication
        } as TaskResponseKnown;

        this.log(
          `WS_TASK_UPDATED: ${taskPath} | Status: ${t.status} → ${task.newStatus} | UpdatedAt: ${updatedTask.updatedAt}`
        );
        return updatedTask;
      }

      return t;
    });

    this.log(
      `WS_UPDATE_COMPLETE: ${taskPath} | Calling addTasksToState with ${updatedTasks.length} tasks`
    );
    this.addTasksToState(updatedTasks);
  };

  /**
   * Adds a listeners
   */
  addEventListeners = (): void => {
    this.log(`addEventListeners: adding websocket listeners`);
    Blac.getBloc(WebSocketBloc).addObserver(
      WebsocketMessageType.taskStatusUpdated,
      (message) => {
        const task = message?.payload as WebsocketTaskStatusUpdatedPayload;
        this.log(`websocket taskStatusUpdated`, task);
        this.handleTaskStatusUpdated(task);
      }
    );

    Blac.getBloc(WebSocketBloc).addObserver(
      WebsocketMessageType.taskUpdated,
      (message) => {
        const task = message?.payload as WebsocketTaskUpdatedPayload;
        this.log(`websocket taskUpdated`, task);
        this.handleTaskUpdated(task);
      }
    );

    Blac.getBloc(WebSocketBloc).addObserver(
      WebsocketMessageType.taskCreated,
      (message) => {
        const task = message?.payload as WebsocketTaskUpdatedPayload;
        this.log(`websocket taskCreated`, task);
        this.handleTaskCreated(task);
      }
    );

    // reload all active proframs when websocket connects

    // reload all active proframs when websocket connects
    Blac.getBloc(WebSocketBloc).addObserver(
      WebsocketLifecycleEvent.connected,
      () => {
        this.log(
          `websocket connected, reloading active programs:`,
          this.activePrograms
        );
        this.activePrograms.forEach((program) => {
          void this.loadProgramTasks(program);
        });
      }
    );
  };

  /**
   * Parses the additional data of a task and returns the details.
   * @param task The task to get the details for.
   * @returns The details of the task.
   */
  getTaskDetails = (task?: TaskResponseKnown): AppTaskDetails => {
    const appTaskDetails: AppTaskDetails = {};
    if (!task) return appTaskDetails;

    const additionalData = task.additionalData as
      | AppTaskResponseAdditionalFields
      | undefined;

    // Handle Dates
    if (task.availableFrom) {
      appTaskDetails.availableDate = new Date(task.availableFrom);
    }

    if (task.availableTo) {
      appTaskDetails.availableDateEnd = new Date(task.availableTo);
    }

    if (task.startedAt) {
      appTaskDetails.startedDate = new Date(task.startedAt);
    }

    if (task.completedAt) {
      appTaskDetails.completedDate = new Date(task.completedAt);
    }

    if (appTaskDetails.availableDate) {
      const now = new Date();
      const { availableDate } = appTaskDetails;

      appTaskDetails.startsInFuture = availableDate > now;

      if (appTaskDetails.startsInFuture) {
        appTaskDetails.timeTillAvailable = new Date(
          availableDate.getTime() - now.getTime()
        );
      }
    }

    if (appTaskDetails.availableDateEnd) {
      const now = new Date();
      const { availableDateEnd } = appTaskDetails;

      if (availableDateEnd > now) {
        appTaskDetails.timeTillAvailableEnd = new Date(
          availableDateEnd.getTime() - now.getTime()
        );
      }
    }

    // Handle Video
    if (additionalData?.vimeoVideoId) {
      appTaskDetails.videoId = additionalData.vimeoVideoId;
      appTaskDetails.videoProvider = "vimeo";
      appTaskDetails.type = "video";
    }

    // Handle Questionnaire
    if (additionalData?.questionnaireRef) {
      appTaskDetails.questionnaireId = additionalData.questionnaireRef.id;
      appTaskDetails.type = "questionnaire";
    }

    return appTaskDetails;
  };

  validTaskTransitions: Partial<
    Record<TaskResponse.status, TaskResponse.status[]>
  > = {
    [TaskResponse.status.SKIPPED]: [],
    [TaskResponse.status.COMPLETED]: []
  };

  checkStatusTransition = (params: {
    from?: TaskResponse.status;
    to: TaskResponse.status;
  }): boolean => {
    const { from, to } = params;
    if (!from) {
      return true;
    }

    if (from === to) {
      return false;
    }

    const validTransitions = this.validTaskTransitions[from];
    if (!validTransitions) {
      return true;
    }

    if (validTransitions.includes(to)) {
      return true;
    }

    return false;
  };

  /**
   * Updates the status of a task.
   * @param task The task to update.
   * @param status The new status.
   */
  updateTaskStatus = async (
    task: Partial<TaskResponse> | TaskResponseKnown,
    status: UpdateTaskRequest.status,
    skipReload = false
  ): Promise<TaskResponseKnown | undefined> => {
    try {
      this.log(`updateTaskStatus: to ${status}`, task);
      if (!task.program || !task.group || !task.slug) {
        this.log(
          `updateTaskStatus: Invalid task, missing program, group or slug`,
          task
        );
        throw new Error("Invalid task, missing program, group or slug");
      }

      const currentTask = this.state.tasks.find(
        (t) =>
          t.program === task.program &&
          t.group === task.group &&
          t.slug === task.slug
      );

      const statusTransitionValid = currentTask
        ? this.checkStatusTransition({
            from: currentTask.status,
            to: status
          })
        : true;
      if (!statusTransitionValid) {
        this.log(
          `updateTaskStatus: Invalid status transition from ${currentTask?.status} to ${status}`,
          task
        );
        // eslint-disable-next-line no-console
        console.error(
          `updateTaskStatus: Invalid status transition from ${currentTask?.status} to ${status}`,
          task
        );
        return;
      }

      const updatedTasks = [...this.state.tasks].map((t) => {
        if (this.getTaskPath(t) === this.getTaskPath(task)) {
          return {
            ...t,
            status
          } as TaskResponseKnown;
        }

        return t;
      });
      this.addTasksToState(updatedTasks);

      const updateResponse = await TaskControllerService.updateTaskBySlug(
        task.program,
        task.group,
        task.slug,
        {
          status
        }
      );

      // update all all tasks for the program - await to ensure fresh data before returning
      if (!skipReload) {
        void this.loadProgramTasks(task.program as KnownProgram);
      }

      return updateResponse.data as TaskResponseKnown;
    } catch (error) {
      reportErrorSentry(error);
    }
  };

  getProgramTasks = (program?: KnownProgram): TaskResponseKnown[] => {
    return this.state.tasks.filter((task) => task.program === program);
  };

  isProgramLoaded = (program: KnownProgram): boolean => {
    return this.activePrograms.has(program);
  };

  getProgramsTasks = (programs: KnownProgram[]): TaskResponseKnown[] => {
    const tasks = programs
      .map((program) => this.getProgramTasks(program))
      .flat();
    return tasks;
  };

  getCompletedProgramsTasks = (programs: KnownProgram[]) => {
    const completedTasks = this.getProgramsTasks(programs).filter(
      (task) => task.status === TaskResponse.status.COMPLETED
    );
    return completedTasks;
  };

  /**
   * Gets all tasks that are available.
   * @param program If provided, only tasks for this program will be returned.
   */
  getTaskByStatus = (
    status?: TaskResponse.status[],
    program?: KnownProgram
  ): TaskResponseKnown[] => {
    const programTasks = program
      ? this.getProgramTasks(program)
      : this.state.tasks;
    return programTasks.filter((task) => status?.includes(task.status));
  };

  /** Get a task by its key */
  getTask = (key: TaskKey): TaskResponseKnown | undefined => {
    const task = this.state.tasks.find((task) =>
      this.getTaskPath(task).includes(key)
    );
    return task;
  };

  /**
   * Enable or disable task consolidation
   */
  setConsolidationEnabled = (enabled: boolean): void => {
    this.emit({
      ...this.state,
      consolidationEnabled: enabled
    });
  };

  /**
   * Onboarding tasks that should be consolidated into a single task
   */
  private readonly onboardingTasksToShow = [
    TaskKey.PHARMACY_INSURANCE,
    TaskKey.MEDICAL_INSURANCE,
    TaskKey.CHECKIN_QUESTIONNAIRE, // recurring task
    TaskKey.SELECT_PCP,
    TaskKey.PREFERRED_PHARMACY
  ];

  /**
   * Consolidate onboarding account setup tasks into a single task
   */
  private consolidateOnboardingAccountSetupTasks = (
    tasks: TaskResponseKnown[]
  ): TaskResponseKnown[] => {
    const relevantOnboardingTasks = tasks.filter((task) => {
      // For checkin-questionnaire, only consider it as onboarding task if it's in post_sign_up group
      if (task.slug === TaskKey.CHECKIN_QUESTIONNAIRE) {
        return task.group === "post_sign_up";
      }
      return this.onboardingTasksToShow.includes(task.slug as TaskKey);
    });

    if (relevantOnboardingTasks.length === 0) {
      return tasks;
    }

    const hasNonTerminalOnboardingTask = relevantOnboardingTasks.some(
      (task) =>
        task.status !== TaskResponse.status.COMPLETED &&
        task.status !== TaskResponse.status.SKIPPED
    );

    const remainingTasks = tasks.filter((task) => {
      // For checkin-questionnaire, include it if it's in checkin group (recurring)
      if (task.slug === TaskKey.CHECKIN_QUESTIONNAIRE) {
        return task.group === "checkin";
      }
      return !this.onboardingTasksToShow.includes(task.slug as TaskKey);
    });

    if (hasNonTerminalOnboardingTask) {
      const consolidatedTask: TaskResponseKnown = {
        id: `synthetic-${TaskKey.ONBOARDING_CONSOLIDATED}`,
        slug: TaskKey.ONBOARDING_CONSOLIDATED,
        status: TaskResponse.status.AVAILABLE,
        program: KnownProgram.ONBOARDING,
        group: "consolidated",
        additionalData: {
          title: "task.consolidated_finish_account_setup",
          iconType: "checked-document",
          popupType: "onboardingTasks"
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        uiElements: {
          title: "task.consolidated_finish_account_setup",
          iconType: "checked-document" as TaskUiElementsResponse.iconType,
          popupType: "onboardingTasks"
        },
        behavior: {
          userCanSee: true,
          userCanSkip: true,
          adminCanSkip: true,
          adminCanComplete: true
        }
      };
      return [consolidatedTask, ...remainingTasks];
    }

    return remainingTasks;
  };

  /**
   * Consolidate onboarding labs task when labs step is in progress
   */
  private consolidateOnboardingLabsTask = (
    tasks: TaskResponseKnown[]
  ): TaskResponseKnown[] => {
    try {
      const onboardingBloc = Blac.getBloc(OnboardingBloc);
      const labsStatus = onboardingBloc.statusStepGetYourLabsDone();

      if (labsStatus === "IN_PROGRESS") {
        const consolidatedTask: TaskResponseKnown = {
          id: `synthetic-${TaskKey.ONBOARDING_LABS_CONSOLIDATED}`,
          slug: TaskKey.ONBOARDING_LABS_CONSOLIDATED,
          status: TaskResponse.status.AVAILABLE,
          program: KnownProgram.ONBOARDING,
          group: "consolidated",
          additionalData: {
            title: "task.consolidated_review_lab_status",
            iconType: "clipboard-vial",
            actionCall:
              TaskActionCall.onboardingBlocCtaActionStepGetYourLabsDone
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          uiElements: {
            title: "task.consolidated_review_lab_status",
            iconType: "clipboard-vial" as TaskUiElementsResponse.iconType,
            popupType: "initialLab"
          },
          behavior: {
            userCanSee: true,
            userCanSkip: true,
            adminCanSkip: true,
            adminCanComplete: true
          }
        };
        return [consolidatedTask, ...tasks];
      }
    } catch (error) {
      this.log("Error getting onboarding bloc for labs consolidation", error);
    }

    return tasks;
  };

  /**
   * Consolidate GLP continuation tasks into a single task
   *
   * Logic:
   * 1. Hide when NO GLP continuation group tasks (group: glp1-continuation) are present
   * 2. Show when ANY GLP continuation group task is present
   * 3. Show when all GLP continuation group tasks are terminal BUT pharmacy tasks are still non-terminal
   * 4. Hide when all tasks (GLP continuation group + pharmacy) are in terminal state
   */
  private readonly consolidateGlpContinuationTasks = (
    tasks: TaskResponseKnown[]
  ): TaskResponseKnown[] => {
    const relevantTasks = tasks.filter((task) =>
      glpContinuationTasksToShow.includes(task.slug as TaskKey)
    );

    // Separate GLP continuation group tasks from pharmacy tasks
    const glpContinuationGroupTasks = relevantTasks.filter(
      (task) => task.group === KnownGroup.GLP1_CONTINUATION
    );

    // Hide consolidated task when NO GLP continuation group tasks are present
    if (glpContinuationGroupTasks.length === 0) {
      return tasks;
    }

    // Check if all relevant tasks (GLP + pharmacy) are in terminal state
    const allTasksTerminal = relevantTasks.every(
      (task) =>
        task.status === TaskResponse.status.COMPLETED ||
        task.status === TaskResponse.status.SKIPPED
    );

    // If all tasks (GLP continuation group + pharmacy) are in terminal state, don't show consolidated task
    if (allTasksTerminal) {
      return tasks;
    }

    // Filter out only GLP continuation group tasks (not pharmacy tasks)
    // Pharmacy tasks should remain in the result for further consolidation
    const remainingTasks = tasks.filter(
      (task) => task.group !== KnownGroup.GLP1_CONTINUATION
    );

    // Show consolidated task since there are GLP continuation tasks and not all are terminal
    // Find the first GLP continuation task to get medication name
    const taskWithMedicationName = relevantTasks.find(
      (task) => task.additionalData?.medication_display_name
    );

    const medicationName =
      (taskWithMedicationName?.additionalData
        ?.medication_display_name as string) || "medication";
    const title = `Continue your ${medicationName}`;

    const consolidatedTask: TaskResponseKnown = {
      id: `synthetic-${GLP_CONTINUATION_CONSOLIDATED}`,
      slug: GLP_CONTINUATION_CONSOLIDATED,
      status: TaskResponse.status.AVAILABLE,
      program: KnownProgram.ONBOARDING,
      group: "consolidated",
      additionalData: {
        title,
        iconType: "rx-form",
        popupType: AppPopup.glpContinuation
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      uiElements: {
        title,
        iconType: "rx-form" as TaskUiElementsResponse.iconType,
        popupType: AppPopup.glpContinuation
      },
      behavior: {
        userCanSee: true,
        userCanSkip: false,
        adminCanSkip: true,
        adminCanComplete: true
      }
    };
    return [consolidatedTask, ...remainingTasks];
  };

  /**
   * Consolidate GLP1 refill task - always show it even if LOCKED, and open prepareRefill popup
   * do not show if COMPLETED or SKIPPED
   */
  private consolidateGlp1RefillTask = (
    tasks: TaskResponseKnown[]
  ): TaskResponseKnown[] => {
    if (!featureFlags.consistencyScoreEnabled) {
      return tasks;
    }

    // Only consolidate when consistency score data is available (HWJ/Evernorth users)
    if (!this.hasConsistencyScoreData()) {
      return tasks;
    }

    const glp1RefillTask = tasks.find(
      (task) =>
        task.program === KnownProgram.CARE &&
        task.group === KnownGroup.PRESCRIPTION &&
        task.slug === TaskKey.GLP1_REFILL_SURVEY
    );

    if (!glp1RefillTask) {
      return tasks;
    }

    // Do not show consolidated task if the original task is COMPLETED or SKIPPED
    if (
      glp1RefillTask.status === TaskResponse.status.COMPLETED ||
      glp1RefillTask.status === TaskResponse.status.SKIPPED
    ) {
      return tasks;
    }

    // Always create a consolidated task that shows the prepare refill popup
    const consolidatedTask: TaskResponseKnown = {
      id: TaskKey.GLP1_REFILL_SURVEY_CONSOLIDATED,
      slug: TaskKey.GLP1_REFILL_SURVEY_CONSOLIDATED,
      status: TaskResponse.status.AVAILABLE, // Always show as available
      program: KnownProgram.CARE,
      group: KnownGroup.PRESCRIPTION,
      additionalData: {
        ...glp1RefillTask.additionalData,
        popupType: AppPopup.prepareRefill
      },
      createdAt: glp1RefillTask.createdAt,
      updatedAt: glp1RefillTask.updatedAt,
      uiElements: {
        ...glp1RefillTask.uiElements,
        popupType: AppPopup.prepareRefill
      },
      behavior: {
        userCanSee: true,
        userCanSkip: false,
        adminCanSkip: false,
        adminCanComplete: false
      }
    };

    // Filter out the original GLP1 refill task and add the consolidated one
    const remainingTasks = tasks.filter(
      (task) =>
        !(
          task.program === KnownProgram.CARE &&
          task.group === KnownGroup.PRESCRIPTION &&
          task.slug === TaskKey.GLP1_REFILL_SURVEY
        )
    );

    return [consolidatedTask, ...remainingTasks];
  };

  /**
   * Check if consistency score data has been successfully fetched for the user.
   * GLP1 refill consolidation should only happen for users where the backend
   * returns consistency score data (e.g., HWJ/Evernorth).
   */
  private readonly hasConsistencyScoreData = (): boolean => {
    return Boolean(consistencyScoreState.state.data);
  };

  /**
   * Apply all consolidation rules to tasks
   */
  private applyConsolidation = (
    tasks: TaskResponseKnown[]
  ): TaskResponseKnown[] => {
    if (!this.state.consolidationEnabled) {
      return tasks;
    }

    let consolidatedTasks = tasks;
    // consolidate GLP continuation tasks first to ensure it contains the pharmacy tasks
    consolidatedTasks = this.consolidateGlpContinuationTasks(consolidatedTasks);
    consolidatedTasks =
      this.consolidateOnboardingAccountSetupTasks(consolidatedTasks);
    consolidatedTasks = this.consolidateOnboardingLabsTask(consolidatedTasks);
    consolidatedTasks = this.consolidateGlp1RefillTask(consolidatedTasks);
    return consolidatedTasks;
  };

  /**
   * Get all consolidated tasks (synthetic + consolidated)
   */
  get consolidatedTasks(): TaskResponseKnown[] {
    const syntheticTasks = this.syntheticTasks;
    const allTasks = [
      ...syntheticTasks,
      ...this.state.tasks.filter(
        (task) =>
          task.program !== KnownProgram.LIFEBALANCE &&
          task.program !== KnownProgram.EDUCATION
      )
    ];
    return this.applyConsolidation(allTasks);
  }

  private isFirstTask = (task: TaskResponseKnown): boolean => {
    return PROGRAM_FIRST_LESSON[task.program] === task.group + "_" + task.slug;
  };

  /**
   * Generate the appropriate link for a task based on its program
   */
  private generateTaskLink = (task: TaskResponseKnown): string => {
    return `/app/${Path.journey}/?group=${task.group}&slug=${task.slug}`;
  };

  get syntheticTasks(): TaskResponseKnown[] {
    const syntheticTasks: TaskResponseKnown[] = [];

    for (const task of this.state.tasks) {
      const fullTaskKey = `${task.program}.${task.group}.${task.slug}`;
      if (fullTaskKey === TaskKey.RXDIET_MEAL_PLAN_SETUP) {
        const newTask: TaskResponseKnown = {
          ...task,
          uiElements: {
            ...task.uiElements,
            popupType: "link"
          }
        };
        syntheticTasks.push(newTask);
      }

      if (this.educationPrograms.includes(task.program as KnownProgram)) {
        const isAvailable = task.status === TaskResponse.status.AVAILABLE;
        const isInProgress = task.status === TaskResponse.status.IN_PROGRESS;

        if (!(isAvailable || isInProgress)) {
          continue;
        }

        const isCoachingTask = task.slug.includes("coaching");
        const isQuestionnaireTask = task.slug.includes("questionnaire");

        let title = task.uiElements?.title ?? "";
        let iconType = task.uiElements?.iconType ?? "";

        if (isCoachingTask) {
          title = translate("appointment.scheduleAppointment", {
            context: task.additionalData?.appointment_type
          });
          iconType = "rd" as TaskUiElementsResponse.iconType;
        } else if (isQuestionnaireTask) {
          title = translate(
            this.isFirstTask(task)
              ? "prompt.educational.start"
              : "prompt.educational.complete_weight"
          );

          iconType = "education" as TaskUiElementsResponse.iconType;
        }

        const link = this.generateTaskLink(task);

        const newTask: TaskResponseKnown = {
          ...task,
          additionalData: {
            title,
            iconType,
            link
          },
          uiElements: {
            title,
            iconType,
            popupType: "link"
          }
        };
        syntheticTasks.push(newTask);
      }
    }
    return syntheticTasks;
  }

  /** Check if the task status is final */
  isTaskStatusFinal = (task: TaskResponseKnown | undefined): boolean => {
    return (
      task?.status === TaskResponse.status.COMPLETED ||
      task?.status === TaskResponse.status.SKIPPED
    );
  };

  /** Debugger helper functions */
  mockProgramResponsesEnabled =
    localStorage.getItem("mockProgramResponsesEnabled") === "true";
  mockProgramName = localStorage.getItem("mockProgramName") ?? "";
  mockProgramResponses: Record<string, TaskResponseKnown[] | undefined> =
    localStorage.getItem("mockProgramResponses")
      ? (JSON.parse(
          localStorage.getItem("mockProgramResponses") as string
        ) as typeof this.mockProgramResponses)
      : {};

  setMockProgramResponsesEnabled = (enabled: boolean): void => {
    if (this.mockProgramResponsesEnabled === enabled) {
      return;
    }
    this.mockProgramResponsesEnabled = enabled;
    if (enabled) {
      warn("DEBUG: MOCK PROGRAM RESPONSE ENABLED", enabled);
    }
    localStorage.setItem("mockProgramResponsesEnabled", String(enabled));
  };

  setMockResponseForProgram = (program: string, response: string): void => {
    try {
      const parsedResponse = JSON.parse(response) as TaskResponseKnown[];
      if (!Array.isArray(parsedResponse)) {
        throw new Error("Parsed response is not an array");
      }
      this.mockProgramResponses[program] = parsedResponse;
      this.mockProgramName = program;

      localStorage.setItem("mockProgramName", program);
      localStorage.setItem(
        "mockProgramResponses",
        JSON.stringify(this.mockProgramResponses)
      );

      console.log("Mock response set for program", program);
      console.log("Mock response", parsedResponse);
      // dispatch event to notify observers

      this.addTasksToState(parsedResponse, program as KnownProgram);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error parsing mock response", error);
    }
  };

  getMockedProgramTasks = (
    program: string
  ): TaskResponseKnown[] | undefined => {
    if (this.mockProgramResponsesEnabled) {
      const mockedTasks = this.mockProgramResponses[program];
      if (mockedTasks) {
        log(
          `Using mocked program tasks for ${program}`,
          program,
          mockedTasks.length,
          mockedTasks
        );
        return mockedTasks;
      }
    }
    return undefined;
  };
}
